class ComparationTabs {
  constructor(section) {
    this.section = section;
    this.sectionId = section.id;
    this.modal = document.getElementById('ct-modal-' + this.sectionId);
    this.compareSwiper = null;

    this._initTabs();
    this._initSwatches();
    this._initCompareModal();
    this._initMobileSwatchOverlay();
    this._initDotPositioning();
    this._initOverflowDetection();

    // Recalculate dot positioning on resize
    window.addEventListener('resize', () => this._initDotPositioning());
  }

  /* ---- Dot positioning ---- */

  _initDotPositioning(specificPanel = null) {
    const panels = specificPanel ? [specificPanel] : this.section.querySelectorAll('[data-panel]');

    panels.forEach(panel => {
      const imageZone = panel.querySelector('.ct-image-zone');
      if (!imageZone) return;

      const dotWraps = panel.querySelectorAll('.ct-dot-wrap');
      const infoWidth = 271; // Fixed width of the text container

      dotWraps.forEach(wrap => {
        // Store original style if not already stored
        if (!wrap.dataset.originalStyle) {
          wrap.dataset.originalStyle = wrap.getAttribute('style');
        }

        const align = wrap.getAttribute('data-align');
        const originalStyle = wrap.dataset.originalStyle;
        const leftMatch = originalStyle.match(/left:([^;%]+)%/);

        if (!leftMatch) return;

        const baseLeft = parseFloat(leftMatch[1]);

        // Only apply offset on desktop
        if (window.innerWidth >= 750 && align === 'left') {
          const imageZoneWidth = imageZone.offsetWidth;
          const textWidthPercent = (infoWidth / imageZoneWidth) * 100;
          const newLeft = baseLeft - textWidthPercent;
          const newStyle = originalStyle.replace(/left:[^;%]+%/, `left:${newLeft}%`);
          wrap.setAttribute('style', newStyle);
        } else {
          // Restore original style on mobile
          wrap.setAttribute('style', originalStyle);
        }
      });
    });
  }

  /* ---- Overflow detection ---- */

  _initOverflowDetection() {
    // Check on initial load
    this._checkOverflow();

    // Check on window resize
    window.addEventListener('resize', () => this._checkOverflow());

    // Check after a short delay (let images load)
    setTimeout(() => this._checkOverflow(), 500);
  }

  _checkOverflow() {
    const panels = this.section.querySelectorAll('[data-panel]');
    panels.forEach(panel => {
      const imageZone = panel.querySelector('.ct-image-zone');
      if (!imageZone) return;

      const dotSet = panel.querySelector('.ct-dot-set.active');
      if (!dotSet) return;

      const dotInfos = dotSet.querySelectorAll('.ct-dot-info');
      let hasOverflow = false;

      dotInfos.forEach(info => {
        const rect = info.getBoundingClientRect();
        const isOutOfBounds = rect.left < 0 || rect.right > window.innerWidth;

        if (isOutOfBounds) {
          hasOverflow = true;
        }
      });

      // Toggle mobile view if overflow detected
      panel.classList.toggle('ct-has-overflow', hasOverflow);
    });
  }

  /* ---- Tabs ---- */

  _initTabs() {
    const tabs = this.section.querySelectorAll('[data-tab]');
    const panels = this.section.querySelectorAll('[data-panel]');

    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const idx = tab.dataset.tab;

        tabs.forEach(t => {
          t.classList.remove('active');
          t.setAttribute('aria-selected', 'false');
        });
        panels.forEach(p => {
          p.classList.remove('active');
          p.setAttribute('aria-hidden', 'true');
        });

        tab.classList.add('active');
        tab.setAttribute('aria-selected', 'true');

        const panel = this.section.querySelector('[data-panel="' + idx + '"]');
        if (panel) {
          panel.classList.add('active');
          panel.setAttribute('aria-hidden', 'false');
          // Recalculate dot positioning and check for overflow after tab change
          setTimeout(() => {
            this._initDotPositioning(panel);
            this._checkOverflow();
          }, 100);
        }

        // Sync ct-impact-mobile in header
        this._updateImpactMobile(tab);
      });
    });
  }

  _updateImpactMobile(tab) {
    const mobileWrap = this.section.querySelector('.ct-impact-mobile');
    if (!mobileWrap) return;

    const iconSrc = tab.dataset.impactIcon || '';
    const label   = tab.dataset.impactLabel || '';

    const existingImg = mobileWrap.querySelector('.ct-impact-icon');
    const existingSpan = mobileWrap.querySelector('.ct-impact-label');

    if (iconSrc) {
      if (existingImg) {
        existingImg.src = iconSrc;
        existingImg.style.display = '';
      } else {
        const img = document.createElement('img');
        img.src = iconSrc;
        img.alt = '';
        img.className = 'ct-impact-icon';
        img.width = 28;
        img.height = 28;
        mobileWrap.insertBefore(img, existingSpan);
      }
    } else if (existingImg) {
      existingImg.style.display = 'none';
    }

    if (existingSpan) existingSpan.textContent = label;
  }

  /* ---- Swatches ---- */

  _initSwatches() {
    this.section.addEventListener('click', (e) => {
      // 1. Overlay click (close)
      if (e.target.closest('.ct-swatch-overlay-backdrop')) {
        
        const swatchesWrapper = this.section.querySelector('.ct-swatches-wrapper');
        const swatchesArea = this.section.querySelector('.ct-swatches-area');

        if (swatchesArea && swatchesWrapper) {
          swatchesArea.classList.remove('expanded');
          swatchesWrapper.classList.remove('expanded');
        }

        return;
      }
      // Handle mobile swatch trigger toggle
      const mobileSwatchTrigger = e.target.closest('.ct-mobile-swatch-trigger');
      if (mobileSwatchTrigger) {
        const swatchesWrapper = mobileSwatchTrigger.closest('.ct-swatches-wrapper');
        if (swatchesWrapper) {
          const swatchesArea = swatchesWrapper.querySelector('.ct-swatches-area');
          if (swatchesArea) {
            swatchesArea.classList.toggle('expanded');
            swatchesWrapper.classList.toggle('expanded');
          }
        }
        return;
      }
      

      const swatch = e.target.closest('.ct-swatch');
      if (!swatch) return;

      const productId  = swatch.dataset.productId;
      const productUrl = swatch.dataset.productUrl;
      const colorName  = swatch.dataset.colorName;
      const panel      = swatch.closest('[data-panel]');
      if (!panel) return;

      this._activateSwatch(panel, productId, productUrl, colorName);

      // Close mobile swatch expanded view
      const swatchesArea = panel.querySelector('.ct-swatches-area');
      const swatchesWrapper = panel.querySelector('.ct-swatches-wrapper');
      if (swatchesArea) {
        swatchesArea.classList.remove('expanded');
      }
      if (swatchesWrapper) {
        swatchesWrapper.classList.remove('expanded');
      }
    });
  }

  _activateSwatch(panel, productId, productUrl, colorName) {
    const panelIdx = panel.dataset.panel;

    // Swatches (both list and overlay share the same data attrs)
    let activeSwatch = null;
    panel.querySelectorAll('.ct-swatch').forEach(s => {
      const isActive = s.dataset.productId === productId;
      s.classList.toggle('active', isActive);
      if (isActive) activeSwatch = s;
    });

    // Main image
    panel.querySelectorAll('.ct-img').forEach(img => {
      img.classList.toggle('active', img.dataset.productId === productId);
    });

    // Dot set
    panel.querySelectorAll('.ct-dot-set').forEach(ds => {
      ds.classList.toggle('active', ds.dataset.productId === productId);
    });

    // Mobile points list
    panel.querySelectorAll('.ct-mobile-pts').forEach(mp => {
      mp.classList.toggle('active', mp.dataset.productId === productId);
    });

    // Color name display (mobile trigger label)
    const colorDisplay = panel.querySelector('[data-color-name-display]');
    if (colorDisplay) colorDisplay.textContent = colorName;

    // Update mobile swatch image
    if (activeSwatch && activeSwatch.dataset.swatchImg) {
      const mobileSwatchImg = panel.querySelector('.ct-mobile-swatch-img');
      if (mobileSwatchImg) {
        mobileSwatchImg.src = activeSwatch.dataset.swatchImg;
        mobileSwatchImg.alt = colorName;
      }
    }

    // Shop Now URL
    const shopBtn = panel.querySelector('[data-shop-btn]');
    if (shopBtn) shopBtn.href = productUrl;

    // Sync compare modal images + shop btns for this column
    if (this.modal) {
      [
        this.modal.querySelector('[data-compare-col="' + panelIdx + '"]'),
        this.modal.querySelector('[data-compare-slide="' + panelIdx + '"]'),
      ].forEach(col => {
        if (!col) return;
        col.querySelectorAll('.ct-modal-img').forEach(img => {
          img.classList.toggle('active', img.dataset.productId === productId);
        });
        const btn = col.querySelector('[data-compare-shop-btn]');
        if (btn) btn.href = productUrl;
      });
    }

    // Check for overflow after swatch change
    setTimeout(() => this._checkOverflow(), 100);
  }

  /* ---- Compare modal ---- */

  _initCompareModal() {
    // Open
    this.section.querySelectorAll('[data-open-compare]').forEach(btn => {
      btn.addEventListener('click', () => this._openCompareModal());
    });

    if (!this.modal) return;

    // Close via backdrop or close button
    this.modal.addEventListener('click', (e) => {
      if (e.target.closest('[data-close-compare]')) this._closeCompareModal();
    });

    // ESC
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.getAttribute('aria-hidden') === 'false') {
        this._closeCompareModal();
      }
    });
  }

  _openCompareModal() {

    if (!this.modal) return;
    this.modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    // Init Swiper on mobile only, lazily
    if (!this.compareSwiper && window.innerWidth < 750 && typeof Swiper !== 'undefined') {
      this._initCompareSwiper();
    }
  }

  _closeCompareModal() {
    if (!this.modal) return;
    this.modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  _initCompareSwiper() {
    const el = this.modal.querySelector('.ct-modal-swiper');
    if (!el) return;

    this.compareSwiper = new Swiper(el, {
      slidesPerView: 2,
      spaceBetween: 0,
      loop: true,
      navigation: {
        nextEl: el.querySelector('.swiper-button-next'),
        prevEl: el.querySelector('.swiper-button-prev'),
      },
    });
  }

  /* ---- Mobile swatch overlay ---- */

  _initMobileSwatchOverlay() {
    this.section.addEventListener('click', (e) => {
      // Open
      const openBtn = e.target.closest('[data-open-swatch-overlay]');
      if (openBtn) {
        this._openMobileSwatchOverlay(openBtn.dataset.openSwatchOverlay);
        return;
      }
      // Close
      const closeEl = e.target.closest('[data-close-swatch-overlay]');
      if (closeEl) {
        this._closeMobileSwatchOverlay(closeEl.dataset.closeSwatchOverlay);
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.section.querySelectorAll('.ct-swatch-overlay[aria-hidden="false"]').forEach(ov => {
          this._closeMobileSwatchOverlay(ov.dataset.swatchOverlay);
        });
      }
    });
  }

  _openMobileSwatchOverlay(panelIdx) {
    const overlay = this.section.querySelector('[data-swatch-overlay="' + panelIdx + '"]');
    if (!overlay) return;
    overlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  _closeMobileSwatchOverlay(panelIdx) {
    const overlay = this.section.querySelector('[data-swatch-overlay="' + panelIdx + '"]');
    if (!overlay) return;
    overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }
}

/* ---- Boot ---- */

function initComparationTabss() {
  document.querySelectorAll('[id^="comparation-tabs--"]').forEach(section => {
    if (!section._dcInstance) {
      section._dcInstance = new ComparationTabs(section);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initComparationTabss);
} else {
  initComparationTabss();
}

document.addEventListener('shopify:section:load', (e) => {
  const section = e.target.querySelector('[id^="comparation-tabs--"]');
  if (section) {
    section._dcInstance = null;
    section._dcInstance = new ComparationTabs(section);
  }
});
